// Drives the password form on the Login tab of the clinician's profile
// (templates/clinic_user/profile/login/show.html.slim) using Hanko's profile flow directly, rather
// than the <hanko-profile> web component, so that the tab only offers what we want clinicians to
// change and matches the rest of the profile. The element with `data-hanko-password` carries the
// runtime config:
//
//   data-hanko-api-url   Hanko project base URL (from settings.hanko_public_url)
//   data-hanko-user-id   Hanko id of the logged-in user
//
// Hanko offers `password_create` to a user without a password and `password_update` to a user with
// one. Both take the new password and return to the profile's start, so the same form serves both.

import { Hanko } from "@teamhanko/hanko-elements";

// Keep this flow's cached state apart from the login widget's (which uses Hanko's default key).
const FLOW_CACHE_KEY = "hanko-password-flow-state";

async function mountPasswordForm(container) {
  const hanko = new Hanko(container.dataset.hankoApiUrl);
  const loadingSection = container.querySelector("[data-password-loading]");
  const passwordForm = container.querySelector("[data-password-form]");
  const logInAgainSection = container.querySelector("[data-password-log-in-again]");
  const unavailableSection = container.querySelector("[data-password-unavailable]");
  const failedSection = container.querySelector("[data-password-failed]");
  const passwordInput = passwordForm.querySelector("[data-password-input]");
  const confirmationInput = passwordForm.querySelector("[data-password-confirmation-input]");
  const errorElement = passwordForm.querySelector("[data-password-error]");
  const errorMessageElement = errorElement.querySelector("[data-password-error-message]");
  const savedElement = passwordForm.querySelector("[data-password-saved]");
  const submitButton = passwordForm.querySelector("button[type='submit']");

  // Each flow state's actions can only be run once, so this always holds the latest state.
  let currentState;

  function showSection(section) {
    [loadingSection, passwordForm, logInAgainSection, unavailableSection, failedSection].forEach((element) => {
      element.hidden = element !== section;
    });
  }

  function showFailure(error) {
    console.error("Password form failed", error);
    showSection(failedSection);
  }

  // Move on from a state. Returns false (after showing the failure screen) if Hanko returned an error.
  function enterState(state) {
    currentState = state;
    if (state.name === "error") {
      showFailure(state.error);
      return false;
    }
    return true;
  }

  // The action Hanko offers for the user's password: creating one, or updating the one they have
  function passwordAction() {
    const { password_create: passwordCreate, password_update: passwordUpdate } = currentState.actions;
    if (passwordUpdate.enabled) return passwordUpdate;
    if (passwordCreate.enabled) return passwordCreate;
    return null;
  }

  // Show the form worded for the action Hanko offers, with Hanko's length limits on the inputs
  function showPasswordForm() {
    const action = passwordAction();
    if (!action) {
      showSection(unavailableSection);
      return;
    }

    const mode = action.name === "password_create" ? "create" : "update";
    passwordForm.querySelectorAll("[data-password-mode]").forEach((element) => {
      element.hidden = element.dataset.passwordMode !== mode;
    });

    const passwordLimits = action.inputs.password;
    [passwordInput, confirmationInput].forEach((input) => {
      if (passwordLimits?.min_length) input.minLength = passwordLimits.min_length;
      if (passwordLimits?.max_length) input.maxLength = passwordLimits.max_length;
    });

    showSection(passwordForm);
  }

  function showPasswordError(message) {
    errorMessageElement.textContent = message;
    errorElement.hidden = false;
  }

  function passwordErrorMessage(error, passwordLimits) {
    if (error.code === "value_too_short_error" && passwordLimits?.min_length) {
      return `Your password must be at least ${passwordLimits.min_length} characters long.`;
    }
    if (error.code === "value_too_long_error" && passwordLimits?.max_length) {
      return `Your password must be no more than ${passwordLimits.max_length} characters long.`;
    }
    return error.message || "Something went wrong. Please try again.";
  }

  container.querySelector("[data-password-retry]").addEventListener("click", () => window.location.reload());

  passwordForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submitButton.disabled) return;
    errorElement.hidden = true;
    savedElement.hidden = true;

    if (passwordInput.value !== confirmationInput.value) {
      showPasswordError("Those passwords don't match. Please enter them again.");
      return;
    }

    submitButton.disabled = true;

    try {
      const action = passwordAction();
      const nextState = await action.run({ password: passwordInput.value });
      if (!enterState(nextState)) return;

      // Hanko returns to the profile's start either way. A rejected password comes back as an error
      // on the action's input, or on the state itself.
      const rejectedPassword = nextState.actions[action.name].inputs.password;
      const passwordError = rejectedPassword?.error ?? nextState.error;
      if (passwordError) {
        showPasswordError(passwordErrorMessage(passwordError, rejectedPassword));
        passwordInput.focus();
      } else {
        passwordForm.reset();
        savedElement.hidden = false;
        // The user has a password now, so the form changes from adding one to changing it
        showPasswordForm();
      }
      submitButton.disabled = false;
    } catch (error) {
      showFailure(error);
    }
  });

  // The Hanko session expires separately from ours, and the browser could be logged in to Hanko as
  // someone else, so check we're changing the password of the right account.
  const session = await hanko.validateSession();
  if (!session.is_valid || session.user_id !== container.dataset.hankoUserId) {
    showSection(logInAgainSection);
    return;
  }

  const profileState = await hanko.createState("profile", { cacheKey: FLOW_CACHE_KEY, loadFromCache: false });
  if (!enterState(profileState)) return;

  showPasswordForm();
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-hanko-password]").forEach((container) => {
    mountPasswordForm(container).catch((error) => {
      console.error("Failed to load password form", error);
      container.querySelector("[data-password-loading]").hidden = true;
      container.querySelector("[data-password-failed]").hidden = false;
    });
  });
});
